import { useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ASSET_KINDS, newId, type AssetKind } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { typeLabel, useAssetTypeLabels, useAssets, useAssetTypes } from '../data/fleet.ts'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import { Banner, Field, Pill } from '../ui/components.tsx'
import { OutOfServicePill } from './SafetyPanel.tsx'
import { MachinePortrait, usePortraits } from '../photos/Portrait.tsx'
import { useBlobs } from '../photos/usePhotos.ts'
import { Picto } from '../ui/pictos.tsx'

export { useAssets }

type Status = 'active' | 'retired' | 'all'

/** The club's machine types as a select (M23 item 5): the label in the person's language, the code as the value; a code the club has no row for
 *  (an imported one) stays offered as itself so an edit never loses it. */
export function TypeSelect({ id, value, onChange, testId }: { id: string; value: string; onChange: (code: string) => void; testId?: string }) {
  const { t, i18n } = useTranslation()
  const types = useAssetTypes() ?? []
  const labels = useAssetTypeLabels(i18n.language)
  const codes = [...new Set([...types.filter((ty) => !ty.archived_at).map((ty) => ty.code), ...(value ? [value] : [])])].sort((a, b) => typeLabel(labels, a).localeCompare(typeLabel(labels, b), i18n.language))
  return (
    <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      <option value="">{t('assets.typeNone')}</option>
      {codes.map((c) => <option key={c} value={c}>{typeLabel(labels, c)}</option>)}
    </select>
  )
}

export function AssetsPage() {
  const { t, i18n } = useTranslation()
  const assets = useAssets()
  const labels = useAssetTypeLabels(i18n.language)
  const manage = useWriteAccess('asset.manage')
  const imp = useWriteAccess('asset.import')
  const service = useWriteAccess('service.manage')
  const record = useWriteAccess('meter.record')
  const catalogue = useWriteAccess('catalog.read')
  const [q, setQ] = useState('')
  const [kind, setKind] = useState<AssetKind | 'all'>('all')
  const [status, setStatus] = useState<Status>('active')
  const portraits = usePortraits()
  const blobs = useBlobs()
  const shown = useMemo(() => {
    const n = q.trim().toLowerCase()
    return (assets ?? []).filter((a) => {
      if (status !== 'all' && (a.lifecycle ?? 'active') !== status) return false
      if (kind !== 'all' && a.kind !== kind) return false
      return !n || [a.readable_tag, a.make, a.model, a.serial_no, a.asset_type_code, typeLabel(labels, a.asset_type_code)].some((v) => (v ?? '').toLowerCase().includes(n))
    })
  }, [assets, q, kind, status, labels])
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-bold"><Picto name="mower" className="size-7 text-brand dark:text-fairway" />{t('assets.title')}</h1>
        {manage.permitted && <Link to="/assets/new" className="btn btn-primary w-full sm:w-auto" data-testid="new-asset">{t('assets.new')}</Link>}
      </div>
      {/* M23 item 24: only what the profile may use (the server refuses anyway); item 59: the catalogue one tap from the register for the workshop */}
      <div className="flex flex-wrap gap-2 text-sm" data-testid="assets-toolbar">
        <Link to="/scan" className="btn !min-h-11 !px-4">{t('assets.scan')}</Link>
        {imp.permitted && <Link to="/import" className="btn !min-h-11 !px-4" data-testid="go-import">{t('assets.import')}</Link>}
        {manage.permitted && <Link to="/labels" className="btn !min-h-11 !px-4" data-testid="go-labels">{t('assets.labels')}</Link>}
        {manage.permitted && <Link to="/types" className="btn !min-h-11 !px-4" data-testid="go-types">{t('assets.types')}</Link>}
        {(service.permitted || record.permitted) && <Link to="/service" className="btn !min-h-11 !px-4" data-testid="go-service">{t('assets.service')}</Link>}
        <Link to="/workshop" className="btn !min-h-11 !px-4" data-testid="go-workshop">{t('assets.workshop')}</Link>
        {manage.permitted && catalogue.permitted && <Link to="/catalogue" className="btn !min-h-11 !px-4" data-testid="go-catalogue">{t('assets.catalogue')}</Link>}
      </div>
      <input className="input" type="search" placeholder={t('assets.searchPlaceholder')} aria-label={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="grid grid-cols-2 gap-3">
        <select className="input" aria-label={t('assets.filterKind')} value={kind} onChange={(e) => setKind(e.target.value as AssetKind | 'all')}>
          <option value="all">{t('assets.allKinds')}</option>
          {ASSET_KINDS.map((k) => <option key={k} value={k}>{t(`assets.kinds.${k}`)}</option>)}
        </select>
        <select className="input" aria-label={t('assets.filterStatus')} value={status} onChange={(e) => setStatus(e.target.value as Status)}>
          {(['active', 'retired', 'all'] as const).map((s) => <option key={s} value={s}>{t(`assets.lifecycle.${s}`)}</option>)}
        </select>
      </div>
      {assets && <p className="meta" data-testid="asset-count">{t('assets.count', { count: shown.length })}</p>}
      {assets && shown.length === 0 && <p className="opacity-70">{t('assets.empty')}</p>}
      <ul className="flex flex-col gap-2" data-testid="asset-list">
        {shown.map((a) => (
          <li key={a.id}>
            <Link to="/assets/$assetId" params={{ assetId: a.id }} className="row-link !p-3" data-testid="asset-row" data-tag={a.readable_tag}>
              <MachinePortrait asset={a} link={portraits.get(a.id)} blob={blobs.get(portraits.get(a.id)?.sha256 ?? '')} />
              <div className="min-w-0 flex-1">
                <p className="text-lg font-bold">{a.readable_tag}</p>
                <p className="truncate text-sm opacity-80">{[a.make, a.model].filter(Boolean).join(' ') || t(`assets.kinds.${a.kind}`)}</p>
                {/* the type wraps (review of M23 item 5: "Tondeuse de fa…" at 375 told the jardinier nothing a code did not) */}
                <p className="meta">{t(`assets.kinds.${a.kind}`)}{a.asset_type_code ? ` · ${typeLabel(labels, a.asset_type_code)}` : ''}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                {a.lifecycle !== 'retired' && <OutOfServicePill assetId={a.id} />}
                {a.lifecycle === 'retired' && <Pill tone="gray">{t('assets.retiredPill')}</Pill>}
                {a._pending && <Pill tone="amber">{t('assets.pending')}</Pill>}
              </div>
              <Picto name="chevron" className="size-5 shrink-0 opacity-40" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

export function AssetNewPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { db, send } = useClub()
  const access = useWriteAccess('asset.manage')
  const assetId = useRef(newId())              // minted once per form: a double tap is the same command (idempotent)
  const [f, setF] = useState({ tag: '', kind: 'machine' as AssetKind, make: '', model: '', serial: '', year: '', acquired: '', type: '' })
  const [tagError, setTagError] = useState<string | undefined>()
  const { busy, error, run } = useAction()
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }))

  function submit(e: FormEvent) {
    e.preventDefault(); setTagError(undefined)
    void run(async () => {
      const tag = f.tag.trim().toUpperCase()
      // advisory only: the server decides (the mirror may be stale), but a visible duplicate is cheaper to fix now
      const known = (await db.asset.where('readable_tag').equals(tag).count()) + (await db.overlay.filter((o) => o.row.readable_tag === tag && o.id !== assetId.current).count())
      if (known > 0) { setTagError(t('assets.tagTaken')); return }
      const opt = (v: string) => (v.trim() ? v.trim() : undefined)
      try {
        await send('asset.register@1', {
          asset_id: assetId.current, readable_tag: f.tag.trim(), kind: f.kind, asset_type_code: opt(f.type), make: opt(f.make), model: opt(f.model),
          serial_no: opt(f.serial), year: f.year.trim() ? Number(f.year) : undefined, acquired_on: opt(f.acquired),
        }, { aggregateId: assetId.current })
      } catch (ex) {
        if (ex instanceof Error && 'issues' in ex && (ex as { issues: Array<{ path: unknown[] }> }).issues.some((i) => i.path[0] === 'readable_tag')) { setTagError(t('assets.tagHelp')); return }
        throw ex
      }
      await navigate({ to: '/assets/$assetId', params: { assetId: assetId.current } })
    })
  }

  if (!access.permitted) return <section className="flex flex-col gap-3"><Banner tone="warn">{t('assets.notAllowed')}</Banner><Link to="/assets" className="btn">{t('common.back')}</Link></section>
  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">{t('assets.new')}</h1>
      {access.readOnlyMode && <Banner tone="warn">{t('platform.readOnly')}</Banner>}
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label={t('assets.tag')} hint={t('assets.tagHelp')} error={tagError} htmlFor="tag">
          <input id="tag" className="input uppercase" required maxLength={32} autoCapitalize="characters" value={f.tag} onChange={set('tag')} />
        </Field>
        <Field label={t('assets.kind')} htmlFor="kind">
          <select id="kind" className="input" value={f.kind} onChange={set('kind')}>{ASSET_KINDS.map((k) => <option key={k} value={k}>{t(`assets.kinds.${k}`)}</option>)}</select>
        </Field>
        <Field label={`${t('assets.assetType')} (${t('common.optional').toLowerCase()})`} htmlFor="type">
          <TypeSelect id="type" value={f.type} onChange={(code) => setF((s) => ({ ...s, type: code }))} testId="asset-type" />
        </Field>
        <Field label={`${t('assets.make')} (${t('common.optional').toLowerCase()})`} htmlFor="make"><input id="make" className="input" maxLength={80} value={f.make} onChange={set('make')} /></Field>
        <Field label={`${t('assets.model')} (${t('common.optional').toLowerCase()})`} htmlFor="model"><input id="model" className="input" maxLength={80} value={f.model} onChange={set('model')} /></Field>
        <Field label={`${t('assets.serial')} (${t('common.optional').toLowerCase()})`} htmlFor="serial"><input id="serial" className="input" maxLength={80} value={f.serial} onChange={set('serial')} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('assets.year')} htmlFor="year"><input id="year" className="input" inputMode="numeric" pattern="[0-9]{4}" maxLength={4} value={f.year} onChange={set('year')} /></Field>
          <Field label={t('assets.acquired')} htmlFor="acquired"><input id="acquired" className="input" type="date" value={f.acquired} onChange={set('acquired')} /></Field>
        </div>
        {error && <Banner tone="error">{error}</Banner>}
        <button className="btn btn-primary" disabled={busy || access.readOnlyMode} data-testid="register-asset">{t('assets.register')}</button>
      </form>
    </section>
  )
}
